import { useRef } from 'react';
import { m, useReducedMotion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import HexShape from './HexShape';

const steps = [
  {
    num: '01',
    title: 'Listen',
    body: 'We learn your business first. The way you work, the people you serve, the problems you solve. Every build starts with a real conversation, not a template.',
  },
  {
    num: '02',
    title: 'Build',
    body: 'We design and build something made entirely around you. No templates, no shortcuts. Software your team actually loves to use, every single day.',
  },
  {
    num: '03',
    title: 'Deliver',
    body: "We stay close through every step. Iterate, refine, and hand it over when it's truly ready. Then we're still there when you need us.",
  },
];

export default function Process() {
  const shouldReduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });

  const mouseX = useMotionValue(50);
  const springX = useSpring(mouseX, { stiffness: 40, damping: 18 });

  const handleMouseMove = (e) => {
    if (shouldReduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mouseX.set(((e.clientX - r.left) / r.width) * 100);
  };

  const h1y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const h2y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const h1x = useTransform(springX, [0, 100], [-10, 10]);
  const h2x = useTransform(springX, [0, 100], [8, -8]);

  const fadeUp = (delay = 0) => ({
    initial: shouldReduce ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.65, delay: shouldReduce ? 0 : delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section
      ref={ref}
      className="bg-surface py-28 sm:py-36 relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <m.div
          className="absolute"
          style={{ top: '-8%', right: '-6%', x: shouldReduce ? 0 : h1x, y: shouldReduce ? 0 : h1y }}
        >
          <HexShape size={320} className="text-primary" style={{ opacity: 0.12 }} />
        </m.div>
        <m.div
          className="absolute"
          style={{ bottom: '-4%', left: '-3%', x: shouldReduce ? 0 : h2x, y: shouldReduce ? 0 : h2y }}
        >
          <HexShape size={200} className="text-primary" style={{ opacity: 0.10 }} />
        </m.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <m.div {...fadeUp()} className="mb-20">
          <p className="text-primary text-xs tracking-[0.2em] uppercase font-medium mb-4">
            How we work
          </p>
          <h2
            className="text-ink font-extrabold leading-[0.93] tracking-tight"
            style={{ fontSize: 'clamp(2.25rem, 4.5vw, 4rem)' }}
          >
            We start by listening.
          </h2>
        </m.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8">
          {steps.map((step, i) => (
            <m.div
              key={step.num}
              {...fadeUp(i * 0.1)}
            >
              <div
                className="font-black text-primary/[0.15] leading-none mb-5 tracking-tight select-none"
                style={{ fontSize: 'clamp(5rem, 9vw, 8rem)' }}
                aria-hidden="true"
              >
                {step.num}
              </div>
              <h3 className="text-ink text-lg font-semibold mb-4">{step.title}</h3>
              <m.div
                className="h-px bg-ink/15 mb-5"
                style={{ originX: 0 }}
                initial={shouldReduce ? false : { scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true, margin: '-20px' }}
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              />
              <p className="text-ink-muted leading-relaxed text-base">{step.body}</p>
            </m.div>
          ))}
        </div>
      </div>
    </section>
  );
}
